import React, { useState, useEffect, useMemo } from 'react'
import Card from '../../../components/card'
import { GET_CP_BY_DATES } from '../../../components/graphql/queries'
import { type CPProps } from '../../../utility/types'
import Popup from '../cp-pop-up'
import Message from '../../../components/message'
import CardBody from '../cards-body'
import { type CardBodyCompProps } from '../utils/types'
import { tradeDateMenu } from '../utils/types'
import { useQueryWithExtendedTime } from '../../../hooks/useQueryWithExtendedTime'
import GenericDropdown from '../../../components/dropdown'
import { parseDollarToNumberCalc } from '../../../utility/formatting'

const RequestCard = () => {
  const today = new Date()
  const [filters, setFilters] = useState({
    selectedTradeDate: 'Today',
    fromDate: new Date().toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0]
  })

  const [selectedIssuers, setSelectedIssuers] = useState<string[]>([])
  const [selectedCards, setSelectedCards] = useState<number[]>([])
  const [mergeModeIssuer, setMergeModeIssuer] = useState<string | null>(null)
  const [showCheckBoxes, setShowCheckBoxes] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const [buttonName, setButtonName] = useState('Select Cards')
  const [message, setMessage] = useState<string | null>(null)
  const [messageColor, setMessageColor] = useState<'red' | 'green' | 'yellow'>('green')
  const [issuerOptions, setIssuerOptions] = useState<string[]>(['All Issuer'])
  const [selectedCardIssuer, setSelectedCardIssuer] = useState<string | null>(null)
  const [forceShowAll, setForceShowAll] = useState(false)
  const [mergeError, setMergeError] = useState<string | null>(null)
  const [availabilityFilter, setAvailabilityFilter] = useState<'all' | 'available' | 'not-available'>('all')

  const { data, refetch, QueryWithExtendedTimeComponent, loading, error } = useQueryWithExtendedTime({
    query: GET_CP_BY_DATES,
    options: {
      variables: {
        from_date: filters.fromDate,
        to_date: filters.toDate
      }
    }
  })

  useEffect(() => {
    if (data?.getCPIssuanceByDates?.cpData) {
      const uniqueIssuers: string[] = Array.from(
        new Set(
          data.getCPIssuanceByDates.cpData
            .map((cp: CPProps) => cp.issuerName)
            .filter((name: string | null | undefined): name is string =>
              name !== null && name !== undefined
            )
        )
      )
      setIssuerOptions(['All Issuer', ...uniqueIssuers])
    }
  }, [data])

  useEffect(() => {
    if (selectedCards.length > 1 && data?.getCPIssuanceByDates?.cpData) {
      const issuers = new Set(
        selectedCards.map(id => {
          const cp = data.getCPIssuanceByDates.cpData.find((cp: CPProps) => cp.cPIssuanceId === id)
          return cp?.issuerName
        }).filter(Boolean)
      )

      if (issuers.size > 1) {
        setMergeError('Cannot merge CPs from different issuers')
        setMessage('Cannot merge CPs from different issuers')
        setMessageColor('red')
      } else {
        setMergeError(null)
      }
    } else {
      setMergeError(null)
    }
  }, [selectedCards, data])

  const handleIssuerChange = (value: string) => {
    setSelectedCards([])
    setSelectedCardIssuer(null)
    setSelectedIssuers(value === 'All Issuer' ? [] : [value])
    setForceShowAll(value === 'All Issuer')
    setMergeError(null)
  }

  const filteredData = useMemo(() => {
    if (!data?.getCPIssuanceByDates?.cpData) return []

    let filteredArray = [...data.getCPIssuanceByDates.cpData]

    if (forceShowAll) {
      return filteredArray
    }
    if (selectedCardIssuer) {
      return filteredArray.filter(cp => cp.issuerName === selectedCardIssuer)
    }
    if (selectedIssuers.length > 0 && selectedIssuers[0] !== 'All Issuer') {
      filteredArray = filteredArray.filter(cp => cp.issuerName === selectedIssuers[0])
    }

    if (availabilityFilter === 'available') {
      filteredArray = filteredArray.filter(cp => parseDollarToNumberCalc(cp.notionalQtyReminder) > 0)
    } else if (availabilityFilter === 'not-available') {
      filteredArray = filteredArray.filter(cp => parseDollarToNumberCalc(cp.notionalQtyReminder) === 0)
    }

    return filteredArray
  }, [data, selectedIssuers, selectedCardIssuer, forceShowAll, availabilityFilter])

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a: CPProps, b: CPProps) => b.cPIssuanceId - a.cPIssuanceId)
  }, [filteredData])

  const handleSelectButtonClick = () => {
    if (!showCheckBoxes) {
      setShowCheckBoxes(true)
      setButtonName('Merge Cards')
      setMergeModeIssuer(null)
      setSelectedCards([])
    } else {
      if (selectedCards.length > 0 && !mergeError) {
        setShowPopup(true)
      }
      setShowCheckBoxes(false)
      setButtonName('Select Cards')
      setMergeModeIssuer(null)
    }
  }

  const handlePopupCancel = (message?: string) => {
    if (message) {
      setMessage(message)
      setMessageColor('green')
    }
    setSelectedCards([])
    setShowPopup(false)
  }

  const handleCardSelect = (issuerName: string) => {
    if (!mergeModeIssuer) {
      setMergeModeIssuer(issuerName)
    }
  }

  const handleCardClick = (cPIssuanceId: number) => {
    if (!showCheckBoxes) {
      setSelectedCards([cPIssuanceId])
      setShowPopup(true)
    }
  }

  const handleTradeDateChange = (value: string) => {
    const newFromDate = new Date(today)

    switch (value) {
      case 'Past 3 Days':
        newFromDate.setDate(today.getDate() - 3)
        break
      case 'Past 7 Days':
        newFromDate.setDate(today.getDate() - 7)
        break
      case 'Past 30 Days':
        newFromDate.setDate(today.getDate() - 30)
        break
      default:
        newFromDate.setDate(today.getDate())
    }

    setFilters({
      ...filters,
      selectedTradeDate: value,
      fromDate: newFromDate.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    })
  }

  if (error) return <QueryWithExtendedTimeComponent />

  return (
    <>
      <div className="flex flex-wrap items-center w-full p-4 bg-gray-100 rounded-lg gap-2">
        <GenericDropdown
          options={tradeDateMenu}
          selectedValue={filters.selectedTradeDate}
          onChange={handleTradeDateChange}
          displayMsg="Select Trade Date"
          className="text-white bg-gray-800 hover:bg-black focus:ring-2 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5"
        />
        <GenericDropdown
          options={issuerOptions}
          selectedValue={(selectedCardIssuer ?? selectedIssuers[0]) || 'All Issuer'}
          onChange={handleIssuerChange}
          displayMsg="Select Issuer"
          className="text-white bg-gray-800 hover:bg-black focus:ring-2 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5"
        />
        <GenericDropdown
          options={['all', 'available', 'not-available']}
          selectedValue={availabilityFilter}
          onChange={(value) => { setAvailabilityFilter(value as 'all' | 'available' | 'not-available') }}
          displayMsg="Filter by Availability"
          className="text-white bg-gray-800 hover:bg-black focus:ring-2 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5"
          labelMap={{
            all: 'All CPs',
            available: 'Available Only',
            'not-available': 'Not Available'
          }}
        />
        <button
          onClick={handleSelectButtonClick}
          disabled={showCheckBoxes && !!mergeError}
          className={`text-white bg-gray-800 hover:bg-black focus:ring-2 focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5 ml-auto ${
            showCheckBoxes && mergeError ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {buttonName}
        </button>
      </div>

      {message && (
        <div>
          <Message
            message={message}
            color={messageColor}
            clearMessage={() => { setMessage(null) }}
            className="w-full p-2"
          />
        </div>
      )}
      <div className="flex flex-wrap justify-around mt-4">
        {loading
          ? (
          <p>Loading data...</p>
            )
          : sortedData.length > 0
            ? (
                sortedData.map((cp: CPProps, index: number) => {
                  const isDisabled = parseDollarToNumberCalc(cp.notionalQtyReminder) === 0
                  return (
                    <Card
                      refetch={refetch}
                      key={index}
                      isCp
                      cpIssuanceId={cp.cPIssuanceId}
                      selectedCards={selectedCards}
                      setSelectedCards={setSelectedCards}
                      showCheckBoxes={showCheckBoxes}
                      onClick={
                        isDisabled || showCheckBoxes
                          ? undefined
                          : () => { handleCardClick(cp.cPIssuanceId) }
                      }
                      CardBodyComponent={(cardBodyProps: CardBodyCompProps) => (
                        <CardBody data={cp} {...cardBodyProps} />
                      )}
                      issuerName={cp.issuerName}
                      onSelectCard={handleCardSelect}
                      isClickable={!isDisabled}
                    />
                  )
                })
              )
            : (
          <p>No data found</p>
              )}
      </div>
      {showPopup && (
        <Popup
          show={showPopup}
          cpId={selectedCards}
          onClose={handlePopupCancel}
          setMessage={setMessage}
          setMessageColor={setMessageColor}
          refetch={refetch}
        />
      )}
    </>
  )
}

export default RequestCard
